import { format } from 'date-fns';
import { FC, Suspense, useEffect } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { FiEdit } from 'react-icons/fi';
import { useParams, useSearchParams } from 'react-router-dom';
import { SubmissionErrorBoundary } from '../../../../../components/errors/SubmissionErrorBoundary';
import { PageContainer } from '../../../../../components/page/PageContainer';
import { PageHeader } from '../../../../../components/page/PageHeader';
import { PageSizer, SizerMode } from '../../../../../components/page/PageSizer';
import { TableLoader } from '../../../../../components/shared/table/TableLoader';
import { patternToEmployeeSchedule } from '../../../../../helpers/BuildWorkingPattern';
import { breadcrumbName } from '../../../../../helpers/Utility';
import {
  ReadEmployee,
  useEmployeeIdWorkingPatternsQuery,
  useGetStaffByIdQuery,
  WorkingPatternPeriods
} from '../../../../../services/gql/graphql.generated';
import strings from '../../../team.translations';
import { ScheduleEditForm } from './ScheduleEditForm';

interface TeamMemberScheduleEditProps {}

export const TeamMemberScheduleEdit: FC<TeamMemberScheduleEditProps> = () => {
  const { userId } = useParams<'userId'>();
  const [searchParams] = useSearchParams();
  const handleError = useErrorHandler();

  const fromTeamCalendar = searchParams.get('from') === 'team-calendar';
  const teamCalendarAnchorDate = searchParams.get('teamCalendarAnchorDate');
  const teamCalendarUrl =
    fromTeamCalendar && teamCalendarAnchorDate
      ? `/team/calendar?anchorDate=${teamCalendarAnchorDate}`
      : '/team/calendar';

  const [{ data: userData, error: userError }, refetchStaff] = useGetStaffByIdQuery({
    variables: { id: userId }
  });

  useEffect(() => {
    if (userId) {
      refetchStaff({ requestPolicy: 'network-only' });
    }
  }, [userId, refetchStaff]);

  // Default to standard cache policy in the config to prevent the Suspense loop,
  // then trigger the network refresh inside a useEffect.
  const [{ data: scheduleData, error: scheduleError }, refetchSchedule] = useEmployeeIdWorkingPatternsQuery({
    variables: {
      dateAndPeriods: {
        date: format(new Date(), 'yyyy-MM-dd'),
        periods: 7
      },
      id: userId
    }
  });

  useEffect(() => {
    if (userId) {
      refetchSchedule({ requestPolicy: 'network-only' });
    }
  }, [userId, refetchSchedule]);

  useEffect(() => {
    if (userError || scheduleError) {
      handleError(userError || scheduleError);
    }
  }, [userError, scheduleError, handleError]);

  const employee = userData!.employee;
  const employeeIdWorkingPatterns = scheduleData!.workingPatternsByEmployeeId;

  const breadcrumbs = fromTeamCalendar
    ? [
        {
          name: strings.teamCalendarPageTitle,
          to: teamCalendarUrl
        },
        {
          name: strings.editSchedule.breadcrumb,
          to: `/team-member/${userId}/set-schedule`
        }
      ]
    : [
        {
          name: strings.teamPageTitle,
          to: '/team'
        },
        {
          name: breadcrumbName(employee as ReadEmployee, strings.employeeName),
          to: `/team-member/${userId}`
        },
        {
          name: strings.editSchedule.breadcrumb,
          to: `/team-member/${userId}/set-schedule`
        }
      ];

  return (
    <PageContainer>
      <PageHeader
        icon={<FiEdit />}
        pageTitle={strings.editSchedule.editTitle + ': ' + `${employee?.firstName ?? ''} ${employee?.lastName ?? ''}`}
        breadcrumbs={breadcrumbs}
      />
      <PageSizer mode={SizerMode.PAGE}>
        <div className="py-5">
          <SubmissionErrorBoundary>
            {/* This local Suspense wrapper handles everything nicely if the data isn't 
              fully cached yet on entry, showing the loader seamlessly!
            */}
            <Suspense fallback={<TableLoader />}>
              <ScheduleEditForm
                userId={userId!}
                userFirstName={employee?.firstName ?? ''}
                contractTypeId={employee?.contract?.contractTypeId}
                initSchedule={patternToEmployeeSchedule(employeeIdWorkingPatterns as WorkingPatternPeriods)}
              />
            </Suspense>
          </SubmissionErrorBoundary>
        </div>
      </PageSizer>
    </PageContainer>
  );
};
